import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { X, ArrowRight, Inbox } from "lucide-react";
import api from "@/api";
import { useCompare } from "@/hooks/useCompare";
import { ProviderLogo, VerifiedBadge, FeaturedBadge } from "@/components/ProviderCard";

const ROWS = [
  { label: "Provider type", key: "provider_types" },
  { label: "Solution categories", key: "solution_categories" },
  { label: "Device / product types", key: "device_types" },
  { label: "Connectivity", key: "connectivity", mono: true },
  { label: "Sensors", key: "sensors" },
  { label: "Battery / power", key: "battery_life" },
  { label: "Commercial model", key: "commercial_models" },
  { label: "Compliance", key: "compliance" },
  { label: "Industries", key: "industries" },
  { label: "Use cases", key: "use_cases" },
];

export default function Compare() {
  const compare = useCompare();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (compare.ids.length === 0) { setItems([]); setLoading(false); return; }
    setLoading(true);
    api.post("/providers/compare", { ids: compare.ids })
      .then(({ data }) => setItems(data.items || []))
      .finally(() => setLoading(false));
    api.post("/analytics/event", { event_type: "compare_viewed", metadata: { ids: compare.ids } }).catch(()=>{});
  }, [compare.ids]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Side-by-side</div>
          <h1 className="font-display font-extrabold text-3xl text-slate-900 mt-1">Compare providers</h1>
          <p className="text-sm text-slate-500 mt-1">Compare up to 4 providers across key technical and commercial fields.</p>
        </div>
        {items.length > 0 && (
          <div className="flex gap-2">
            <button onClick={compare.clear} className="btn-secondary" data-testid="compare-clear-all">
              <X size={14}/> Clear all
            </button>
            <button onClick={() => navigate(`/request-quotes?ids=${compare.ids.join(",")}`)} className="btn-accent" data-testid="compare-request-quotes">
              Request quotes from selected <ArrowRight size={14}/>
            </button>
          </div>
        )}
      </div>

      {compare.ids.length === 0 && (
        <div className="card-elevated p-12 text-center">
          <Inbox size={32} className="mx-auto text-slate-300"/>
          <div className="mt-3 font-display font-bold text-xl">No providers selected</div>
          <p className="mt-2 text-sm text-slate-500">Browse the directory and click "Compare" on listing cards to add providers here.</p>
          <Link to="/solutions" className="btn-primary mt-6 inline-flex">Browse directory</Link>
        </div>
      )}

      {items.length > 0 && (
        <div className="card-elevated overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left text-xs uppercase tracking-widest text-slate-500 font-semibold p-4 w-44">Field</th>
                {items.map((p) => (
                  <th key={p.id} className="text-left p-4 align-top min-w-[260px]">
                    <div className="flex items-start gap-3">
                      <ProviderLogo provider={p} size={40}/>
                      <div className="min-w-0">
                        <Link to={`/solutions/${p.slug}`} className="font-display font-bold text-slate-900 hover:underline">{p.name}</Link>
                        <div className="text-xs text-slate-500">{p.hq_country}</div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {p.verified && <VerifiedBadge/>}
                          {p.featured && <FeaturedBadge/>}
                        </div>
                      </div>
                      <button onClick={() => compare.remove(p.id)} className="ml-auto text-slate-400 hover:text-slate-900" data-testid={`compare-remove-${p.slug}`}>
                        <X size={16}/>
                      </button>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-slate-100">
                <td className="p-4 text-xs uppercase tracking-widest text-slate-500 font-semibold">Tagline</td>
                {items.map((p) => (
                  <td key={p.id} className="p-4 text-sm text-slate-700">{p.tagline || "—"}</td>
                ))}
              </tr>
              {ROWS.map((r) => (
                <tr key={r.key} className="border-b border-slate-100">
                  <td className="p-4 text-xs uppercase tracking-widest text-slate-500 font-semibold align-top">{r.label}</td>
                  {items.map((p) => (
                    <td key={p.id} className="p-4 align-top">
                      <div className="flex flex-wrap gap-1.5">
                        {(p[r.key] || []).length === 0 && <span className="text-sm text-slate-400">—</span>}
                        {(p[r.key] || []).map((v) => (
                          <span key={v} className={`tag ${r.mono ? "font-mono text-[10px]" : ""}`}>{v}</span>
                        ))}
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
